import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { 
  Bot, Send, Loader2, Sparkles, Heart, AlertTriangle, 
  RefreshCw, X, Shield, Info
} from 'lucide-react';

/**
 * YouthAICompanion - AI chat companion for Young Adults (16-17)
 * Provides supportive conversation with safety guardrails
 */

const SAFETY_DISCLAIMER = `I'm here to listen and support you. Remember:
• I'm an AI assistant, not a therapist
• If you're in crisis, please reach out to 988 (Suicide & Crisis Lifeline)
• Our conversations may be reviewed by your care team for safety`;

const CONVERSATION_STARTERS = [
  { text: "I'm feeling stressed about school", emoji: "📚" },
  { text: "I need help managing my emotions", emoji: "💭" },
  { text: "I want to talk about my goals", emoji: "🎯" },
  { text: "I'm having trouble sleeping", emoji: "😴" },
  { text: "I feel anxious and don't know why", emoji: "😰" },
];

const YouthAICompanion = ({ clientId, token, backendUrl, toast, clientName }) => {
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [showDisclaimer, setShowDisclaimer] = useState(true);
  const [error, setError] = useState(null);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Initialize or load session
  useEffect(() => {
    const savedSession = sessionStorage.getItem(`ai_companion_session_${clientId}`);
    if (savedSession) {
      const session = JSON.parse(savedSession);
      setSessionId(session.id);
      setMessages(session.messages || []);
      setShowDisclaimer(false);
    }
  }, [clientId]);

  const saveSession = (id, msgs) => {
    sessionStorage.setItem(`ai_companion_session_${clientId}`, JSON.stringify({
      id,
      messages: msgs,
      lastUpdated: new Date().toISOString()
    }));
  };

  const startNewSession = () => {
    const newSessionId = `companion_${Date.now()}`;
    setSessionId(newSessionId);
    setMessages([{
      role: 'assistant',
      content: `Hi ${clientName || 'there'}! 👋 I'm your AI companion. I'm here to listen and chat with you about whatever's on your mind. How are you feeling today?`,
      timestamp: new Date().toISOString()
    }]);
    setShowDisclaimer(false);
    saveSession(newSessionId, []);
  };

  const sendMessage = async (messageText = inputValue) => {
    if (!messageText.trim() || isLoading) return;

    const userMessage = {
      role: 'user',
      content: messageText.trim(),
      timestamp: new Date().toISOString()
    };

    const updatedMessages = [...messages, userMessage];
    setMessages(updatedMessages);
    setInputValue('');
    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(`${backendUrl}/api/youth-portal/ai-companion/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          client_id: clientId,
          session_id: sessionId,
          message: messageText.trim(),
          conversation_history: updatedMessages.slice(-10) // Last 10 messages for context
        })
      });

      const data = await response.json();

      if (data.success) {
        const assistantMessage = {
          role: 'assistant',
          content: data.response,
          timestamp: new Date().toISOString(),
          crisis_detected: data.crisis_detected
        };

        const newMessages = [...updatedMessages, assistantMessage];
        setMessages(newMessages);
        saveSession(sessionId, newMessages);

        // If crisis detected, show alert
        if (data.crisis_detected) {
          toast?.warning?.('I noticed you might be going through something difficult. Remember, you can always reach out to 988 for immediate support.');
        }
      } else {
        setError(data.detail || 'Failed to get response');
      }
    } catch (err) {
      console.error('AI Companion error:', err);
      setError('Unable to connect. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const clearConversation = () => {
    setMessages([]);
    setSessionId(null);
    sessionStorage.removeItem(`ai_companion_session_${clientId}`);
    setShowDisclaimer(true);
  };

  // Disclaimer/Welcome Screen
  if (showDisclaimer) {
    return (
      <div className="space-y-6">
        <div className="text-center">
          <div className="w-20 h-20 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center mx-auto mb-4">
            <Bot className="w-10 h-10 text-white" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900">AI Companion</h2>
          <p className="text-gray-600">Your supportive chat buddy</p>
        </div>

        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-800">
                <p className="font-medium mb-2">Before we chat:</p>
                <ul className="space-y-1 text-blue-700">
                  <li>• I'm an AI, not a real therapist</li>
                  <li>• If you're in crisis, please call or text <strong>988</strong></li>
                  <li>• Your therapist may review our chats for your safety</li>
                  <li>• I'm here to listen and support, not give medical advice</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        <Button 
          onClick={startNewSession}
          className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 py-6 text-lg"
        >
          <Sparkles className="w-5 h-5 mr-2" />
          Start Chatting
        </Button>

        <div className="space-y-2">
          <p className="text-sm text-gray-500 text-center">Or start with:</p>
          <div className="flex flex-wrap gap-2 justify-center">
            {CONVERSATION_STARTERS.map((starter, idx) => (
              <Button
                key={idx}
                variant="outline"
                size="sm"
                onClick={() => {
                  startNewSession();
                  setTimeout(() => sendMessage(starter.text), 500);
                }}
                className="text-sm"
              >
                {starter.emoji} {starter.text}
              </Button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Chat Interface
  return (
    <div className="flex flex-col h-[600px]">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b bg-gradient-to-r from-purple-50 to-pink-50">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
            <Bot className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">AI Companion</h3>
            <p className="text-xs text-gray-500">Always here to listen</p>
          </div>
        </div>
        <Button variant="ghost" size="sm" onClick={clearConversation}>
          <RefreshCw className="w-4 h-4 mr-1" />
          New Chat
        </Button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
        {messages.map((message, idx) => (
          <div
            key={idx}
            className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                message.role === 'user'
                  ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white'
                  : 'bg-white border border-gray-200 text-gray-800'
              }`}
            >
              {message.role === 'assistant' && (
                <div className="flex items-center gap-2 mb-1">
                  <Bot className="w-4 h-4 text-purple-500" />
                  <span className="text-xs text-purple-500 font-medium">AI Companion</span>
                </div>
              )}
              <p className="text-sm whitespace-pre-wrap">{message.content}</p>
              {message.crisis_detected && (
                <div className="mt-2 p-2 bg-red-50 rounded-lg border border-red-200">
                  <p className="text-xs text-red-600 flex items-center gap-1">
                    <Heart className="w-3 h-3" />
                    Remember: 988 is always available if you need to talk to someone
                  </p>
                </div>
              )}
            </div>
          </div>
        ))}
        
        {isLoading && (
          <div className="flex justify-start">
            <div className="bg-white border border-gray-200 rounded-2xl px-4 py-3">
              <div className="flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin text-purple-500" />
                <span className="text-sm text-gray-500">Thinking...</span>
              </div>
            </div>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>

      {/* Error */}
      {error && (
        <div className="px-4 py-2 bg-red-50 border-t border-red-200">
          <p className="text-sm text-red-600 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            {error}
          </p>
        </div>
      )}

      {/* Input */}
      <div className="p-4 border-t bg-white">
        <div className="flex gap-2">
          <Textarea
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Type your message..."
            className="resize-none"
            rows={2}
            disabled={isLoading}
          />
          <Button
            onClick={() => sendMessage()}
            disabled={!inputValue.trim() || isLoading}
            className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 px-6"
          >
            <Send className="w-4 h-4" />
          </Button>
        </div>
        <p className="text-xs text-gray-400 mt-2 text-center">
          <Shield className="w-3 h-3 inline mr-1" />
          Your conversations are private but may be reviewed for safety
        </p>
      </div>
    </div>
  );
};

export default YouthAICompanion;
